import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { Tools } from '../_shared/tools';
import { Product } from '../_models/product';
import { ProductContent } from '../_models/product-content';
import { Observable } from 'rxjs';
import { ProductPrice } from '../_models/product-price';
import { ProductPriceAdjustments } from '../_models/product-price-adjustments';
import { ProductCategory } from '../_models/product-category';
import { map } from 'rxjs/operators';
import { BundleItem } from '../_models/bundle-item';

@Injectable()
export class ProductService {
  constructor(private http: HttpClient, public auth: AuthService, public tools: Tools) { }

  public addProduct(product: Product): Promise<Product> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products]);
    console.log(requestUrl);
    return this.http
      .post(requestUrl, product, options)
      .toPromise()
      .then((res) => res as Product);
  }

  public addProductContents(detail: ProductContent): Promise<ProductContent> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, detail.ProductId, 'contents']);
    return this.http
      .post(requestUrl, detail, options)
      .toPromise()
      .then((res) => res as ProductContent);
  }

  public deleteProduct(product: Product): Promise<Product> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, product.Id]);
    return this.http
      .delete(requestUrl, options)
      .toPromise()
      .then((res) => res as Product);
  }

  public deleteProductContents(productId: number, id: number): Promise<ProductContent> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, productId, 'contents', id]);
    return this.http
      .delete(requestUrl, options)
      .toPromise()
      .then((res) => res as ProductContent);
  }

  public getPriceCountryAdjustments(priceCountryId: number, origin: string) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${priceCountryId}/price-override/${origin}`;
    return this.http.get<ProductPriceAdjustments[]>(requestUrl, options);
  }

  public getProductById(id: number): Observable<Product> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, +id]);
    return this.http.get<Product>(requestUrl, options);
  }

  public getProductCategories(id: number): Observable<ProductCategory[]> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, id, 'categories']);
    return this.http.get<ProductCategory[]>(requestUrl, options);
  }

  public getProductContents(id: number): Observable<ProductContent[]> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, id, 'contents']);
    return this.http.get<ProductContent[]>(requestUrl, options);
  }

  public getProductPrices(id: number): Observable<ProductPrice[]> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, id, 'price']);
    return this.http.get<ProductPrice[]>(requestUrl, options);
  }

  public getProducts(): Promise<any[]> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}?t=0&pk=0`;
    return this.http
      .get(requestUrl, options)
      .toPromise()
      .then((res) => res as Product[]);
  }

  public removeProductCategory(categoryId: number, productId: number): Observable<ProductCategory> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${productId}/categories/${categoryId}`;
    return this.http.delete<ProductCategory>(requestUrl, options);
  }

  public saveProductCategory(productCategory: ProductCategory): Observable<ProductCategory[]> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${productCategory.ProductId}/categories`;
    return this.http.post<ProductCategory[]>(requestUrl, productCategory, options);
  }

  public saveProductPrice(price: ProductPrice) {
    if (price.ProductPriceId) {
      return this.updateProductPrice(price);
    }

    return this.addProductPrice(price);
  }

  public saveProductPriceAdjustment(priceAdjustment: ProductPriceAdjustments, priceCountryId: number) {
    if (priceAdjustment.Id) {
      return this.updateProductPriceAdjustment(priceAdjustment, priceCountryId);
    }

    return this.addProductPriceAdjustment(priceAdjustment, priceCountryId);
  }

  public updateProduct(product: Product): Observable<Product> {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, product.Id]);
    return this.http.put<Product>(requestUrl, product, options);
  }

  private addProductPrice(price: ProductPrice) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, price.ProductId, 'price', price.Origin]);
    return this.http.post<ProductPrice>(requestUrl, price, options);
  }

  private addProductPriceAdjustment(priceAdjustment: ProductPriceAdjustments, productId: number) {
    //priceAdjustment.ProductCountryId = priceCountryId;
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${productId}/price-override/${priceAdjustment.Origin}`;
    return this.http.post<ProductPriceAdjustments>(requestUrl, priceAdjustment, options);
  }

  private updateProductPrice(price: ProductPrice) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, price.ProductId, 'price', price.Origin]);
    return this.http.post<ProductPrice>(requestUrl, price, options);
  }

  public deleteProductPrice(price: ProductPrice) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = this.tools.buildUrl([environment.shopApiUrl, environment.apiEndpoints.products, price.ProductId, 'price', price.Origin, price.ProductPriceId]);
    return this.http.delete<any>(requestUrl, options);
  }

  private updateProductPriceAdjustment(priceAdjustment: ProductPriceAdjustments, productId: number) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${productId}/price-override/${priceAdjustment.Origin}/${priceAdjustment.Id}`;
    return this.http.put<ProductPriceAdjustments>(requestUrl, priceAdjustment, options);
  }

  public deleteProductPriceAdjustment(priceAdjustment: ProductPriceAdjustments, productId: number) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${productId}/price-override/${priceAdjustment.Origin}/${priceAdjustment.Id}`;
    return this.http.delete<ProductPriceAdjustments>(requestUrl, options);
  }

  public getPackageBundles(productId: number) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${productId}/package`;
    return this.http.get<BundleItem[]>(requestUrl, options);
  }

  public deletePackageBundles(productId: number, packageId: number) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${productId}/package/${packageId}`;
    return this.http.delete<BundleItem>(requestUrl, options);
  }

  public savePackageBundles(productId: number, product: BundleItem) {
    const header = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.auth.token });
    const options = { headers: header };
    const requestUrl = `${environment.shopApiUrl}/${environment.apiEndpoints.products}/${productId}/package`;
    return this.http.post<BundleItem>(requestUrl, product, options);
  }
}
